import { type HTMLAttributes, type JSX } from 'react';
import { cn } from '../../lib/utils.ts';

export type CardProps = HTMLAttributes<HTMLElement> & {
  readonly interactive?: boolean;
  readonly 'data-testid'?: string;
  /**
   * The element to render. A card is a LOOK, not a `<div>`: a table of contents is a `<nav>`,
   * a row in a list is an `<li>`, and hand-rolling the recipe to keep the right element is how
   * the same surface came to exist in five slightly different versions.
   */
  readonly as?: 'div' | 'nav' | 'li' | 'section' | 'article';
};

export function Card({ as: As = 'div', interactive, className, ...props }: CardProps): JSX.Element {
  return (
    <As
      className={cn(
        'rounded-lg border border-border bg-card p-6 shadow-lift',
        interactive && 'transition-shadow hover:bg-muted/40 hover:shadow-lift-hover',
        className,
      )}
      {...props}
    />
  );
}

export type CardHeaderProps = HTMLAttributes<HTMLDivElement> & {
  readonly 'data-testid'?: string;
};

export function CardHeader({ className, ...props }: CardHeaderProps): JSX.Element {
  return <div className={cn('flex items-center justify-between gap-3', className)} {...props} />;
}

export type CardTitleProps = HTMLAttributes<HTMLHeadingElement> & {
  readonly 'data-testid'?: string;
};

export function CardTitle({ className, ...props }: CardTitleProps): JSX.Element {
  return <h3 className={cn('text-sm font-semibold', className)} {...props} />;
}
